/**
 * Générateur — Calculer la valeur d'une expression littérale contenant une
 * puissance simple.
 * Attendu ID : 931 | Niveau : 5ème | Domaine : Puissances (cluster 4, O33)
 *
 * Source : REPLI SÉSAMATH (décision utilisateur 2026-07-11 — pas d'exercice
 * dans le doc prof, relance en cours). Cahier Sésamath 5e chap. 04, Série 4
 * « Valeur d'une expression » (p59-60), ex. 1-5, 8, 9.
 *
 * Plan de cartes (validé 2026-07-11) :
 *
 *   Carte « x², 3x², (3x)² » — ex. 1 (les tableaux)
 *     texte_trou_saisie (« Pour x = 4 : x² = {{16}} ; 3x² = {{48}} ;
 *     (3x)² = {{144}} ») + tableau bijection (expression ↔ valeur pour un x
 *     donné ; valeurs distinctes par construction pour x ≥ 2, k ≥ 2 :
 *     x² < kx² < (kx)² = k²x²). Variante cube (x³, 2x³, (2x)³) sur x ≤ 3.
 *
 *   Carte « calculer » — ex. 2/3/8 Calculer pour x donné
 *     texte_trou_saisie étapes (« A = 2x³ − 7 pour x = 3 : A = 2 × {{27}}
 *     − 7 = {{54}} − 7 = {{47}} ») + qcm_unique. x = 0 inclus en trous (le
 *     piège de l'ex. 2 : C = 4 + 2x² pour x = 0). Au QCM, x ≥ 3 : pour
 *     x = 2, la misconception « x² lu 2x » donne la BONNE réponse (2² = 2×2),
 *     le piège s'effondre. Distracteurs : (bx)ⁿ, x² lu 2x, oubli du terme
 *     constant.
 *
 *   Carte « parenthèses » — ex. 9, LE cadrage (3x² − 7 vs (3x)² − 7)
 *     texte_trou_saisie (les deux calculs pour le même x) + qcm_unique
 *     (valeur de (bx)² − d : le distracteur principal est bx² − d, l'oubli
 *     des parenthèses).
 *
 *   Carte « tester » — ex. 4/5 Tester une égalité (cadrage explicite)
 *     tableau_vf (« Pour x = 3 : 2x² = 6x » — vérité calculée en entiers,
 *     égalités ax² = bx vraies ssi x = b/a, x² − c = 0, x² = c) +
 *     qcm_unique justification (le « D'une part / d'autre part » de l'ex. 4
 *     fermé en QCM : vraie/fausse AVEC les deux valeurs calculées).
 *     Non repris (hors cadrage, signalé) : ex. 6 (deux variables), ex. 7
 *     (programmes de calcul, recouvert par la carte « calculer »).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, basePayload,
} from '../../../utils';

const ATTENDU_ID = 931;

const L_TABLEAU = 'x², 3x², (3x)²';
const L_CALCULER = 'Calculer pour x donné';
const L_PARENTHESES = 'Avec ou sans parenthèses (littéral)';
const L_TESTER = 'Tester une égalité';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212

// ═════════ Carte 1 — x², 3x², (3x)² (ex. 1) ═════════

/** { x, lignes: [{ expr, val }] } — valeurs distinctes par construction */
const tirerTableauExpressions = () => {
    const variante = randomChoice(['carre', 'cube']);
    if (variante === 'carre') {
        const x = randInt(2, 6);
        const k = randInt(2, 5);
        return {
            x,
            lignes: [
                { expr: 'x²', val: x * x },
                { expr: `${k}x²`, val: k * x * x },
                { expr: `(${k}x)²`, val: (k * x) ** 2 },
            ],
        };
    }
    const x = randInt(2, 3);
    const k = randomChoice([2, 3]);
    return {
        x,
        lignes: [
            { expr: 'x³', val: x ** 3 },
            { expr: `${k}x³`, val: k * x ** 3 },
            { expr: `(${k}x)³`, val: (k * x) ** 3 },
        ],
    };
};

const genTableauTrous = () => {
    const t = tirerTableauExpressions();
    // Une égalité par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // égalités à trous, l'impression coupait la ligne au milieu).
    const phrase = `Pour x = ${t.x} :\n`
        + t.lignes.map(l => `${l.expr} = {{${l.val}}}`).join('\n');
    const payload = makeTexteTrou(phrase,
        '<p><b>Calcule chaque expression pour la valeur de x indiquée.</b> '
        + 'Attention à la place des parenthèses !</p>',
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = t;
    return payload;
};

const genTableauBijection = () => {
    const t = tirerTableauExpressions();
    const entrees = shuffle(t.lignes);
    const lignes = entrees.map(e => e.expr);
    const valeurs = entrees.map(e => String(e.val));
    const colonnes = shuffle([...valeurs]);
    const bonnesReponses = valeurs.map(v => colonnes.indexOf(v));
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const tableElement = {
        type: 'table',
        config: { type: 'association', colonnes, lignes, bonnesReponses },
    };
    const payload = basePayload('qcm',
        `<p><b>Pour x = ${t.x}, associe chaque expression à sa valeur.</b> `
        + 'Chaque colonne ne sert qu\'une fois.</p>',
        { choix: [], bonne_reponse: br },
        opts(L_TABLEAU),
        { version: 1, elements: [tableElement] });
    payload._meta.params = t;
    return payload;
};

// ═════════ Carte 2 — Calculer pour x donné (ex. 2/3/8) ═════════

const genCalculerTrous = () => {
    const forme = randomChoice(['moins', 'plus', 'xZero']);
    const lettre = randomChoice(['A', 'B', 'C']);
    let texte, phrase, params;
    if (forme === 'xZero') {
        // le piège de l'ex. 2 : C = d + b·x² pour x = 0
        const b = randInt(2, 8);
        const d = randInt(2, 15);
        texte = `<p><b>Calcule ${lettre} = ${d} + ${b}x² pour x = 0.</b></p>`;
        phrase = `${lettre} = ${d} + ${b} × {{0}} = {{${d}}}`;
        params = { forme, b, d };
    } else {
        const n = randomChoice([2, 3]);
        const x = n === 2 ? randInt(2, 6) : randInt(2, 4);
        const b = randInt(2, 8);
        const d = randInt(1, 15);
        const expr = `${b}x${n === 2 ? '²' : '³'}`;
        const px = x ** n;
        const prod = b * px;
        if (forme === 'plus') {
            texte = `<p><b>Calcule ${lettre} = ${expr} + ${d} pour x = ${x}.</b></p>`;
            phrase = `${lettre} = ${b} × {{${px}}} + ${d} = {{${prod}}} + ${d} = {{${prod + d}}}`;
            params = { forme, b, d, x, n };
        } else {
            const dOk = Math.min(d, prod - 1);   // résultat positif par construction
            texte = `<p><b>Calcule ${lettre} = ${expr} ${MOINS} ${dOk} pour x = ${x}.</b></p>`;
            phrase = `${lettre} = ${b} × {{${px}}} ${MOINS} ${dOk} = {{${prod}}} ${MOINS} ${dOk} = {{${prod - dOk}}}`;
            params = { forme, b, d: dOk, x, n };
        }
    }
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CALCULER), mode: 'saisie' });
    payload._meta.params = params;
    return payload;
};

const genCalculerQcm = () => {
    // x ≥ 3 : pour x = 2 le piège « x² lu 2x » coïncide avec la bonne réponse
    const n = randomChoice([2, 3]);
    const x = n === 2 ? randInt(3, 6) : randInt(3, 4);
    const b = randInt(2, 8);
    const px = x ** n;
    const correct = String(b * px);
    const expr = `${b}x${n === 2 ? '²' : '³'}`;
    const candidats = shuffle([
        (b * x) ** n,          // (bx)ⁿ : le coefficient entré dans la puissance
        b * x * n,             // xⁿ lu x × n
        b + px,                // + au lieu de ×
    ]).map(String).filter(v => v !== correct);
    const distincts = [...new Set(candidats)].slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct,
        `<p><b>Calcule ${expr} pour x = ${x}.</b></p>`, opts(L_CALCULER));
    payload._meta.params = { b, x, n };
    return payload;
};

// ═════════ Carte 3 — Avec ou sans parenthèses (ex. 9) ═════════

const tirerParametresLitteral = () => {
    const b = randInt(2, 4);
    const x = randInt(2, 6);
    const d = randInt(1, Math.min(10, b * x * x - 1));   // A positif par construction
    return { b, x, d };
};

const genParenthesesLitteralTrous = () => {
    const { b, x, d } = tirerParametresLitteral();
    const A = b * x * x - d;
    const B = (b * x) ** 2 - d;
    const texte = `<p><b>Pour x = ${x}, calcule A = ${b}x² ${MOINS} ${d} et `
        + `B = (${b}x)² ${MOINS} ${d}.</b> Les parenthèses changent tout !</p>`;
    const phrase = `A = ${b} × {{${x * x}}} ${MOINS} ${d} = {{${A}}}\n`
        + `B = ({{${b * x}}})² ${MOINS} ${d} = {{${B}}}`; // une égalité par ligne
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PARENTHESES), mode: 'saisie' });
    payload._meta.params = { b, x, d, A, B };
    return payload;
};

const genParenthesesLitteralQcm = () => {
    const { b, x, d } = tirerParametresLitteral();
    const B = (b * x) ** 2 - d;
    const correct = String(B);
    const candidats = shuffle([
        b * x * x - d,          // l'oubli des parenthèses (LE piège du cadrage)
        (b * x) ** 2 + d,       // signe ignoré
        b * x * 2 - d,          // x² lu 2x
    ]).map(String).filter(v => v !== correct);
    const distincts = [...new Set(candidats)].slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct,
        `<p><b>Calcule B = (${b}x)² ${MOINS} ${d} pour x = ${x}.</b></p>`,
        opts(L_PARENTHESES));
    payload._meta.params = { b, x, d, B };
    return payload;
};

// ═════════ Carte 4 — Tester une égalité (ex. 4/5) ═════════

/** Une ligne V/F « Pour x = k : <égalité> » avec sa vérité calculée. */
const tirerLigneTest = (vraieVoulue) => {
    const forme = randomChoice(['axCarreBx', 'xCarreMoinsC', 'xCarreEgalC']);
    if (forme === 'axCarreBx') {
        // a·x² = b·x, vraie ssi a·k² = b·k, c.-à-d. k = b/a
        const a = randInt(2, 4);
        const x0 = randInt(2, 5);
        const b = a * x0;
        const k = vraieVoulue ? x0 : x0 + randomChoice([1, 2]);
        return { ligne: `Pour x = ${k} : ${a}x² = ${b}x`, vraie: a * k * k === b * k };
    }
    if (forme === 'xCarreMoinsC') {
        // x² − c = 0, vraie ssi c = k²
        const k = randInt(2, 9);
        const c = vraieVoulue ? k * k : k * k + randomChoice([1, 2, 5]);
        return { ligne: `Pour x = ${k} : x² ${MOINS} ${c} = 0`, vraie: k * k === c };
    }
    // x² = c, vraie ssi c = k² (l'ex. 5c du cahier : x² = 10 pour x = 5)
    const k = randInt(2, 9);
    const c = vraieVoulue ? k * k : k * 2;   // le faux « c = 2k » : x² lu 2x
    return { ligne: `Pour x = ${k} : x² = ${c}`, vraie: k * k === c };
};

const genTesterVf = () => {
    const lignes = [];
    const bonnes = [];
    const vues = new Set();
    const nbVrai = randInt(2, 3);
    const voulues = shuffle([
        ...Array(nbVrai).fill(true),
        ...Array(5 - nbVrai).fill(false),
    ]);
    voulues.forEach(vraieVoulue => {
        for (let essai = 0; essai < 30; essai++) {
            const t = tirerLigneTest(vraieVoulue);
            if (t.vraie !== vraieVoulue) continue;         // garde : vérité recalculée
            if (vues.has(t.ligne)) continue;
            vues.add(t.ligne);
            lignes.push(t.ligne);
            bonnes.push(t.vraie ? 0 : 1);
            break;
        }
    });
    const payload = makeVraiFaux(lignes, bonnes,
        '<p><b>Vrai ou faux ?</b> Pour chaque ligne, teste l\'égalité en calculant '
        + 'les deux membres.</p>',
        opts(L_TESTER));
    payload._meta.params = { lignes, bonnes };
    return payload;
};

const genTesterQcm = () => {
    // le « D'une part / d'autre part » de l'ex. 4, fermé en QCM
    const a = randInt(2, 4);
    const x0 = randInt(2, 5);
    const b = a * x0;
    const vraie = randomChoice([true, false]);
    const k = vraie ? x0 : x0 + randomChoice([1, 2]);
    const gauche = a * k * k;
    const droite = b * k;
    const texte = `<p><b>Pour x = ${k}, l'égalité ${a}x² = ${b}x est-elle vraie ?</b> `
        + 'Calcule les deux membres avant de répondre.</p>';
    let correct, candidats;
    if (vraie) {
        correct = `Vraie : les deux membres valent ${gauche}.`;
        candidats = [
            `Fausse : ${a}x² vaut ${(a * k) ** 2} et ${b}x vaut ${droite}.`,   // (ax)² au lieu de ax²
            `Fausse : ${a}x² vaut ${a * k * 2} et ${b}x vaut ${droite}.`,      // x² lu 2x
            'Fausse : une égalité avec x² n\'est jamais vraie.',
        ];
    } else {
        correct = `Fausse : ${a}x² vaut ${gauche} et ${b}x vaut ${droite}.`;
        candidats = [
            `Vraie : les deux membres valent ${gauche}.`,
            `Vraie : les deux membres valent ${droite}.`,
            'Vraie : une égalité avec x est vraie pour tout x.',
        ];
    }
    const distincts = [...new Set(candidats)].filter(c => c !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte, opts(L_TESTER));
    payload._meta.params = { a, b, k, vraie, gauche, droite };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    tableau_texte_trou_saisie: genTableauTrous,
    tableau_tableau_bijection: genTableauBijection,
    calculer_texte_trou_saisie: genCalculerTrous,
    calculer_qcm_unique: genCalculerQcm,
    parentheses_texte_trou_saisie: genParenthesesLitteralTrous,
    parentheses_qcm_unique: genParenthesesLitteralQcm,
    tester_tableau_vf: genTesterVf,
    tester_qcm_unique: genTesterQcm,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM',
    tableau_bijection: 'Tableau', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'calculer', label: L_CALCULER, formatsList: buildFormats('calculer') },
    { id: 'parentheses', label: L_PARENTHESES, formatsList: buildFormats('parentheses') },
    { id: 'tester', label: L_TESTER, formatsList: buildFormats('tester') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
